class SortFacet {
  constructor(component) {
    this.component = component;
    this.elements = {};
    this.eventHandlers = {
      optionSelected: this.#setSortOption.bind(this),
      toggleDrawer: this.#toggleOptionsDrawer.bind(this),
      handleOutsideClick: this.#handleOutsideClick.bind(this),
    };
    this.componentId = null;
    this.sectionId = null;
    this.filtersActive = null;
    this.sortBy = null;
    this.isOpen = false;

    this.#validateRequiredAttributes();
    this.#initialize();
  }

  /* VALIDATION */
  #validateRequiredAttributes() {
    this.componentId = this.component.dataset.componentId;
    this.sectionId = this.component.dataset.sectionId;
    this.filtersActive = this.component.dataset.filtersActive;

    if (!this.componentId) {
      throw new Error(`SortFacet[#validateRequiredAttributes]: Component with id ${this.component.id} must have a data-component-id attribute.`);
    }

    if (!this.sectionId) {
      throw new Error(`SortFacet[#validateRequiredAttributes]: Component with id ${this.component.id} must have a data-section-id attribute.`);
    }

    if (!this.filtersActive) {
      throw new Error(`SortFacet[#validateRequiredAttributes]: Component with id ${this.component.id} must have a data-filters-active attribute.`);
    }
  }

  #validateCachedElements() {
    for (const [key, element] of Object.entries(this.elements)) {
      if (Array.isArray(element)) {
        if (!element.every(el => el instanceof HTMLElement)) {
          throw new Error(`SortFacet[#validateCachedElements]: Invalid array of elements for [${key}]`);
        }
      } else if (!(element instanceof HTMLElement || element instanceof SVGElement)) {
        throw new Error(`SortFacet[#validateCachedElements]: Required element [${key}] not found`);
      }
    }
  }

  /* EVENT HANDLING */
  #bindEvents() {
    this.#removeEventListeners();
    this.elements.toggle.addEventListener('click', this.eventHandlers.toggleDrawer);
    this.elements.form.addEventListener('input', this.eventHandlers.optionSelected);
    document.addEventListener('click', this.eventHandlers.handleOutsideClick);
  }

  #removeEventListeners() {
    this.elements.toggle.removeEventListener('click', this.eventHandlers.toggleDrawer);
    this.elements.form.removeEventListener('input', this.eventHandlers.optionSelected);
    document.removeEventListener('click', this.eventHandlers.handleOutsideClick);
  }

  #handleOutsideClick(event) {
    if (!this.component.contains(event.target) && this.isOpen) {
      this.#toggleOptionsDrawer();
    }
  }

  #toggleOptionsDrawer() {
    this.isOpen = !this.isOpen;
    this.elements.toggle.setAttribute('aria-expanded', this.isOpen);
    this.elements.drawer.setAttribute('tabindex', this.isOpen ? '0' : '-1');

    this.isOpen
      ? (this.elements.drawer.classList.remove('grid-rows-0'), this.elements.drawer.classList.add('grid-rows-1'), this.elements.icon.classList.add('rotate-180'))
      : (this.elements.drawer.classList.remove('grid-rows-1'), this.elements.drawer.classList.add('grid-rows-0'), this.elements.icon.classList.remove('rotate-180'));
  }

  async #setSortOption(event) {
    event.preventDefault();
    const selectedOption = event.target.value;

    this.sortBy = selectedOption;
    this.#toggleOptionsDrawer();
    this.#updateLabelText();
    this.elements.toggle.setAttribute('data-sort-by', selectedOption);
    this.#updateURLParams();

    const html = await this.#fetchSectionHTML();
    this.#renderFromFetch(html);
    
  }

  /* CACHING */
  #cacheElements() {
    this.elements = {
      toggle: this.component.querySelector('[facet-sort-toggle]'),
      drawer: this.component.querySelector('[facet-sort-drawer]'),
      icon: this.component.querySelector('[facet-sort-icon] svg'),
      labels: Array.from(this.component.querySelectorAll('[facet-sort-label]')),
      form: this.component.querySelector('[facet-sort-form]')
    }
  }

  /* SIDE EFFECTS */
  #updateLabelText() {
    this.elements.labels.forEach((label) =>{
      label.dataset.sortOption === this.sortBy
        ? label.classList.remove('none')
        : label.classList.add('none');
    })
  }

  #updateURLParams() {
    const url = new URL(window.location);
    url.searchParams.set('sort_by', this.sortBy);
    window.history.replaceState({}, '', url);
  }

  /* FETCHING & RENDERING */
  async #fetchSectionHTML() {
    const params = new URLSearchParams(window.location.search).toString();
    const sectionUrl = `${window.location.pathname}?section_id=${this.sectionId}&${params}`;

    try {
      const response = await fetch(sectionUrl);
      return await response.text();
    } catch (error) {
      console.error(`SortFacet[#fetchSectionHTML]: Error fetching section HTML: ${error}`);
    }
  }

  #renderFromFetch(html) {
    const hasFilters = this.filtersActive === 'true';
    const section = document.getElementById(`shopify-section-${this.sectionId}`);
    const grid = document.getElementById(`facet-results-${this.componentId}`);
    const filters = document.getElementById(`facet-active-${this.componentId}`);

    if (!section || !grid) {
      throw new Error(`SortFacet[#renderFromFetch]: Sort facet component with id ${this.componentId} must have a section [${section}] and grid element [${grid}].`);
    }

    const renderedSection = new DOMParser().parseFromString(html, 'text/html');

    const renderedGrid = renderedSection.getElementById(`facet-results-${this.componentId}`);
    const renderedFilters = renderedSection.getElementById(`facet-active-${this.componentId}`);


    grid.innerHTML = renderedGrid.innerHTML;

    if (hasFilters && filters) {
      filters.innerHTML = renderedFilters.innerHTML;
    }
  }

  /* INITIALIZATION */
  #initialize() {
    this.#cacheElements();
    this.#validateCachedElements();
    this.#bindEvents();
  }
}

export default SortFacet;